import { Component, OnInit, OnDestroy, Output, EventEmitter } from '@angular/core';
import { Subscription } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { UsersService } from 'src/app/services/users.service';
import { RolesService } from 'src/app/services/roles.service';
import { ToastService } from 'src/app/services/toast.service';
import { AuthService } from 'src/app/services/auth.service';
import { environment } from 'src/environments/environment';
import UserGenerator from 'src/app/classes/user.class';

@Component({
  selector: 'app-user-roles-reassignment',
  templateUrl: './user-roles-reassignment.component.html',
  styleUrls: ['./user-roles-reassignment.component.scss'],
})
export class UserRolesReassignmentComponent implements OnInit, OnDestroy {
  @Output() goBack = new EventEmitter<void>();

  // User Roles workflow properties
  selectedFilterRole: any = null;
  selectedNewRole: any = null;
  availableRoles: any[] = [];

  // User list properties
  userList: any[] = [];
  filteredUserList: any[] = [];
  selectedUsers: any[] = [];
  userSearchText = '';
  selectAllUsers = false;

  // Progress tracking
  isProcessing = false;
  progressMessage = '';
  progressPercentage = 0;
  estimatedTimeRemaining = 0;
  isLoadingStaff = false;

  // Helper method for template
  Math = Math;

  // Subscriptions
  private subscriptions: Subscription[] = [];

  constructor(
    private http: HttpClient,
    private usersService: UsersService,
    private rolesService: RolesService,
    private toastService: ToastService,
    private authService: AuthService
  ) {}

  ngOnInit(): void {
    this.loadAvailableRoles();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  onBackClick(): void {
    this.reset();
    this.goBack.emit();
  }

  // Load available roles for User Roles reassignment
  loadAvailableRoles = () => {
    this.rolesService.getRoles();
    const rolesSubscription = this.rolesService.rolesData$.subscribe(
      (rolesData: any) => {
        if (rolesData?.rows) {
          this.availableRoles = rolesData.rows
            .map((role: any) => ({
              roleName: role.roleName,
              displayName: role.roleName,
              id: role.id,
            }))
            .sort((a: any, b: any) => a.roleName.localeCompare(b.roleName));
        }
      }
    );
    this.subscriptions.push(rolesSubscription);
  };

  // User Roles selection methods
  onFilterRoleSelected = (role: any) => {
    this.selectedFilterRole = role;
    this.loadStaffByRole(role);
  };

  onNewRoleSelected = (role: any) => {
    this.selectedNewRole = role;
  };

  loadStaffByRole = (role: any) => {
    this.isLoadingStaff = true;
    this.userList = [];
    this.filteredUserList = [];
    this.selectedUsers = [];
    this.selectAllUsers = false;
    this.userSearchText = '';

    this.authService.user$.subscribe((user: any) => {
      if (!user || !user.loginName) {
        this.isLoadingStaff = false;
        return;
      }

      const apiUrl = `${environment.expressUrl}/users`;
      const params = {
        loginName: user.loginName,
        activeFlag: 1,
        includedAssociations: 'PrimaryBuilding, PrimaryPosition, Roles',
        limit: 10000,
        offset: 0,
        sortColumnName: 'firstName',
        sortColumnDirection: 'ASC',
        showAssignedToMe: 0,
        customFlag: 0,
      };

      this.http.get(apiUrl, { params }).subscribe({
        next: (response: any) => {
          let users = null;
          if (response?.rows) {
            users = response.rows;
          } else if (response?.data?.rows) {
            users = response.data.rows;
          } else if (response?.data && Array.isArray(response.data)) {
            users = response.data;
          } else if (Array.isArray(response)) {
            users = response;
          }

          if (users && users.length > 0) {
            const usersWithRole = users.filter((user: any) => {
              if (user.Roles && Array.isArray(user.Roles)) {
                return user.Roles.some(
                  (userRole: any) => userRole.roleName === role.roleName
                );
              }
              return false;
            });

            this.userList = usersWithRole;
            this.filteredUserList = [...this.userList];
            this.isLoadingStaff = false;
          } else {
            this.isLoadingStaff = false;
          }
        },
        error: (error) => {
          console.error('Error fetching users for role filtering:', error);
          this.isLoadingStaff = false;
        },
      });
    });
  };

  // User search functionality
  onUserSearchChange = (searchText: string) => {
    this.userSearchText = searchText;

    if (!searchText || searchText.trim() === '') {
      this.filteredUserList = [...this.userList];
    } else {
      this.filteredUserList = this.userList.filter(
        (user) =>
          user.firstName?.toLowerCase().includes(searchText.toLowerCase()) ||
          user.lastName?.toLowerCase().includes(searchText.toLowerCase()) ||
          user.email?.toLowerCase().includes(searchText.toLowerCase())
      );
    }
  };

  // User selection methods
  toggleUserSelection = (user: any) => {
    const index = this.selectedUsers.findIndex((u) => u.id === user.id);
    if (index > -1) {
      this.selectedUsers.splice(index, 1);
    } else {
      this.selectedUsers.push(user);
    }
  };

  toggleSelectAllUsers = () => {
    this.selectAllUsers = !this.selectAllUsers;
    if (this.selectAllUsers) {
      this.selectedUsers = [...this.filteredUserList];
    } else {
      this.selectedUsers = [];
    }
  };

  isUserSelected = (user: any) => {
    return this.selectedUsers.some((u) => u.id === user.id);
  };

  // Assign role to selected users
  assignRoleToUsers = () => {
    if (this.selectedUsers.length === 0 || !this.selectedNewRole) {
      this.toastService.setToast({
        text: 'Please select both users and a new role to assign.',
        type: 'warn',
        icon: true,
      });
      return;
    }

    const currentUser = this.usersService.user;
    const hasUserEdit = currentUser?.Permissions?.USER_EDIT === 1;
    const isManagerOfUsers = this.selectedUsers.every((user: any) => {
      const isPrimaryManager = currentUser?.ManagedPrimaryUsers?.some(
        (managed: any) => managed.id === user.id
      );
      const isSecondaryManager = currentUser?.ManagedSecondaryUsers?.some(
        (managed: any) => managed.id === user.id
      );
      return isPrimaryManager || isSecondaryManager;
    });

    if (!hasUserEdit && !isManagerOfUsers) {
      this.toastService.setToast({
        text: 'You do not have permission to modify user roles. You need USER_EDIT permission or must be a manager of the selected users.',
        type: 'error',
        icon: true,
      });
      return;
    }

    this.isProcessing = true;
    this.progressPercentage = 0;
    this.estimatedTimeRemaining = Math.max(5, this.selectedUsers.length * 0.5);
    this.progressMessage = `Processing bulk role assignment for ${this.selectedUsers.length} users...`;

    setTimeout(() => {
      const progressElement = document.getElementById('progress-indicator');
      if (progressElement) {
        progressElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 100);

    const beforeUnloadHandler = (event: BeforeUnloadEvent) => {
      if (this.isProcessing) {
        event.preventDefault();
        event.returnValue =
          'Bulk role assignment in progress. Are you sure you want to leave?';
        return 'Bulk role assignment in progress. Are you sure you want to leave?';
      }
    };
    window.addEventListener('beforeunload', beforeUnloadHandler);

    const userIds = [
      ...new Set(this.selectedUsers.map((user: any) => Number(user.id))),
    ];

    const newRoleId = this.availableRoles.find(
      (role: any) => role.roleName === this.selectedNewRole.roleName
    )?.id;

    if (!newRoleId) {
      this.toastService.setToast({
        text: 'Error: Could not find the selected role ID. Please try again.',
        type: 'error',
        icon: true,
      });
      this.isProcessing = false;
      this.progressMessage = '';
      return;
    }

    this.processIndividualRoleUpdates(
      userIds,
      newRoleId,
      beforeUnloadHandler
    );
  };

  // Process individual role updates using User class approach
  private async processIndividualRoleUpdates(
    userIds: number[],
    newRoleId: number,
    beforeUnloadHandler: any
  ) {
    try {
      this.progressMessage = `Assigning new role to ${userIds.length} users...`;

      for (let i = 0; i < userIds.length; i++) {
        const userId = userIds[i];

        let currentUserData: any = null;

        this.usersService.clearSelectedUser();
        this.usersService.getUserById(userId, {
          includedAssociations: 'Roles',
        });

        await new Promise((resolve, reject) => {
          const subscription = this.usersService.selectedUserData$.subscribe(
            (userData: any) => {
              if (userData) {
                currentUserData = userData;
                subscription.unsubscribe();
                resolve(userData);
              }
            }
          );

          setTimeout(() => {
            subscription.unsubscribe();
            reject(new Error(`Timeout fetching user data for user ${userId}`));
          }, 5000);
        });

        if (!currentUserData) {
          console.error(`Failed to fetch user data for user ${userId}`);
          continue;
        }

        const user = new UserGenerator(currentUserData);
        user.id = userId;
        user.updateValue('roleIds', newRoleId);
        const requestBody = user.generateRequest();

        await this.usersService.updateUser(requestBody, userId).toPromise();

        const progress = Math.round(((i + 1) / userIds.length) * 90);
        this.progressPercentage = progress;
        this.progressMessage = `Updated ${i + 1} of ${userIds.length} users...`;
      }

      this.progressPercentage = 100;
      this.progressMessage = `Role assignment complete for ${userIds.length} users!`;
      this.estimatedTimeRemaining = 0;

      window.removeEventListener('beforeunload', beforeUnloadHandler);
      this.isProcessing = false;

      this.toastService.setToast({
        text: `✅ Successfully assigned role "${this.selectedNewRole.roleName}" to ${userIds.length} users.`,
        type: 'success',
        icon: true,
      });

      this.usersService.clearSelectedUser();
      this.reset();
      this.goBack.emit();
    } catch (error: any) {
      window.removeEventListener('beforeunload', beforeUnloadHandler);
      this.isProcessing = false;
      this.progressMessage = '';

      console.error('Error in individual role updates:', error);
      this.toastService.setToast({
        text: `❌ Failed to assign role "${this.selectedNewRole.roleName}" to users. Please try again.`,
        type: 'error',
        icon: true,
      });
    }
  }

  reset = () => {
    this.selectedFilterRole = null;
    this.selectedNewRole = null;
    this.userList = [];
    this.filteredUserList = [];
    this.selectedUsers = [];
    this.userSearchText = '';
    this.selectAllUsers = false;
    this.availableRoles = [];
    this.isProcessing = false;
    this.progressMessage = '';
    this.progressPercentage = 0;
    this.estimatedTimeRemaining = 0;
    this.isLoadingStaff = false;
  };
}

